import { formatDate } from "@angular/common"
import { Dictionary } from '@ngrx/entity'
import { oneDayApart } from '../../helpers/helpers'
import { JoiEvent } from '../events.era'
import { DAY_MS } from '../../../../globals/helpers/constants';
import { flatten, sortByProp, str2Date } from "globals/helpers/helpers";

export interface CalendarDay { dateId: string, day: number, otherMonth: boolean }

export const getCalendarWeeks = (date: Date) => {
  date = new Date(date)
  const month = date.getMonth()
  let forwardDate = new Date(date.setDate(1))
  let backwardDate = new Date(forwardDate.getTime() - DAY_MS)
  const calendarDays: CalendarDay[] = []

  while (backwardDate.getDay() !== 6) {
    calendarDays.unshift({ dateId: formatDate(backwardDate, 'yyyy/MM/dd', 'en-au'), day: backwardDate.getDate(), otherMonth: backwardDate.getMonth() !== month })
    backwardDate = new Date(backwardDate.getTime() - DAY_MS)
  }

  while (forwardDate.getMonth() === month || forwardDate.getDay() !== 0) {
    calendarDays.push({ dateId: formatDate(forwardDate, 'yyyy/MM/dd', 'en-au'), day: forwardDate.getDate(), otherMonth: forwardDate.getMonth() !== month })
    forwardDate = new Date(forwardDate.getTime() + DAY_MS)
  }

  const nWeeks = Math.floor(calendarDays.length / 7)
  const weeks: CalendarDay[][] = []
  for (let w = 0; w < nWeeks; w++) {
    weeks.push(calendarDays.slice(w*7, (w+1)*7))
  }
  return weeks
}




export const nextDateOneDayAfter = (event: JoiEvent, dateId: string): boolean => {
  const index = event.dates.indexOf(dateId)
  if (index === -1) return false
  const nextDate = event.dates[event.dates.indexOf(dateId) + 1]
  return nextDate && oneDayApart(dateId, nextDate)
}

export const getEventIdsByDateId = (events: Dictionary<JoiEvent>, includedEventIds: Set<string>): { [dateId: string]: string[] } => {
  const eventList = Object.values(events).filter(e => includedEventIds.has(e.eventId) && e.first_date)
  const dateList = flatten(eventList.map(e => e.dates.map(date => ({ eventId: e.eventId, date }))))
  const sortedDateList = dateList.sort(sortByProp('date'))
  const eventIdsByDateId = {}
  sortedDateList.forEach(({ eventId, date }) => {
    if (!eventIdsByDateId[date]) eventIdsByDateId[date] = []
    eventIdsByDateId[date].push(eventId)
  })
  return eventIdsByDateId
}

export const getNumberOfConsecutiveDaysFromDateIdAndEvent = (event: JoiEvent, dateId: string): number => {
  const { dates } = event
  const dateIndex = dates.indexOf(dateId)
  if (dateIndex === -1) return 0
  let datePointer = dateIndex
  let currentDate = dateId
  while (nextDateOneDayAfter(event, currentDate)) {
    datePointer++
    currentDate = event.dates[datePointer]
  }
  return datePointer - dateIndex + 1
}


export interface CalendarEvent { eventId: string, start?: boolean, end: boolean, daySpan?: number, endsInWeek: boolean }

export const sortNewOnDateIdEventsByConsectiveDaysFromDateId = (events: JoiEvent[], dateId: string): CalendarEvent[]  => {
  // console.log({ events })
  const consecutiveDaysAfterDateIdAndNewEventList = events.map(event => ({ event, consecutiveDays: getNumberOfConsecutiveDaysFromDateIdAndEvent(event, dateId) })).sort((a, b) => b.consecutiveDays - a.consecutiveDays)
  // console.log({ consecutiveDaysAfterDateIdAndEventList: consecutiveDaysAfterDateIdAndNewEventList })
  const day = str2Date(dateId).getDay()
  return consecutiveDaysAfterDateIdAndNewEventList.map(({ event, consecutiveDays }) => ({ start: true, eventId: event.eventId, end: consecutiveDays === 1, daySpan: Math.min(7 - day, consecutiveDays), endsInWeek: consecutiveDays <= 7 - day }))
}

export const getEventCalendarDateBlocksMap = (events: Dictionary<JoiEvent>, includedEventIds: Set<string>): Dictionary<CalendarEvent[]> => {
  const eventIdsByDateId = getEventIdsByDateId(events, includedEventIds)
  const dateIds = Object.keys(eventIdsByDateId).sort()
  // console.log({ dateIds, eventIdsByDateId, includedEventIds })

  const eventBlocksByDateId: Dictionary<CalendarEvent[]> = {}
  let previousDateBlock: CalendarEvent[] = []
  let previousDateId = "1980/11/28"
  dateIds.forEach(dateId => {
    previousDateBlock = oneDayApart(previousDateId, dateId) ? previousDateBlock : []
    const eventIds = eventIdsByDateId[dateId]
    let newIds: string[] = []
    const currentDateBlock: CalendarEvent[] = []
    const day = str2Date(dateId).getDay()
    if (!previousDateBlock || !previousDateBlock.length) {
      newIds = eventIds
    } else {
      eventIds.forEach(eventId => {
        const previousIndex = previousDateBlock.findIndex(calendarEvent => calendarEvent && calendarEvent.eventId === eventId)
        if (previousIndex === -1) return newIds.push(eventId)
        const event = events[eventId]
        const consecutiveDays = getNumberOfConsecutiveDaysFromDateIdAndEvent(event, dateId)
        currentDateBlock[previousIndex] = { eventId, end: consecutiveDays === 1, daySpan: Math.min(7 - day, consecutiveDays), endsInWeek: consecutiveDays <= 7 - day }
      })
      // console.log({ currentDateBlockWithPreviousOnly: jsonCopyObject(currentDateBlock) })
    }
    const newCalendarEvents = sortNewOnDateIdEventsByConsectiveDaysFromDateId(newIds.map(eventId => events[eventId]), dateId)
    let index = 0
    newCalendarEvents.forEach(calendarEvent => {
      while (currentDateBlock[index]) index++
      currentDateBlock[index] = calendarEvent
    })
    previousDateBlock = currentDateBlock
    previousDateId = dateId
    eventBlocksByDateId[dateId] = currentDateBlock

  })

  return eventBlocksByDateId

}
